import * as Dialog from '@radix-ui/react-dialog';
import React, { useState } from 'react';

import { getDisplayError } from '../../utils/get-display-error';
import { BaseButton, BaseButtonProps, type ButtonSize } from '../button/BaseButton';
import { Button } from '../button/Button';

interface IConfirmDialogBaseProps {
  title: string;
  description: React.ReactNode;
  cancelText?: string;
  submitText: string;
  onSubmit: () => void | Promise<void>;
  secondarySubmitText?: string;
  onSecondarySubmit?: () => void | Promise<void>;
}

/** The dialog draws its own trigger button — the usual case. */
interface IConfirmDialogTriggerProps extends IConfirmDialogBaseProps {
  triggerText: React.ReactNode;
  triggerColor?: BaseButtonProps['color'];
  triggerSize?: ButtonSize;
  /** Tooltip / accessible name for an icon-only trigger. */
  triggerTitle?: string;
  isOpen?: never;
  onClose?: never;
}

/** No trigger: the caller opens the dialog itself, e.g. from a menu item. */
interface IConfirmDialogControlledProps extends IConfirmDialogBaseProps {
  isOpen: boolean;
  onClose: () => void;
  triggerText?: never;
  triggerColor?: never;
  triggerSize?: never;
  triggerTitle?: never;
}

export type IConfirmDialogProps = IConfirmDialogTriggerProps | IConfirmDialogControlledProps;

export const ConfirmDialog: React.FC<IConfirmDialogProps> = (props) => {
  const {
    triggerText,
    triggerColor,
    triggerSize,
    triggerTitle,
    title,
    cancelText = 'Annuleer',
    submitText,
    description,
    onSubmit,
    secondarySubmitText,
    onSecondarySubmit,
  } = props;
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenState, setIsOpenState] = useState(false);

  // Controlled when the caller owns the open state (a menu item opened us); otherwise the trigger does.
  const isOpen = props.isOpen ?? isOpenState;

  const close = () => {
    setIsOpenState(false);
    props.onClose?.();
  };

  const runSubmit = async (handler: () => void | Promise<void>) => {
    setIsLoading(true);
    try {
      await handler();
      close();
    } catch (err) {
      setError(getDisplayError(err));
    }
    setIsLoading(false);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && close()}>
      {triggerText != null && (
        <Button
          isLoading={isLoading}
          color={triggerColor}
          size={triggerSize}
          title={triggerTitle}
          ariaLabel={triggerTitle}
          onTrigger={(evt) => {
            evt.preventDefault();
            evt.stopPropagation();

            setIsOpenState(true);
          }}
        >
          {triggerText}
        </Button>
      )}

      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title className="heading-one mb-4">{title}</Dialog.Title>

          <Dialog.Description asChild>
            <div className="mb-4">{description}</div>
          </Dialog.Description>

          <div className="text-sm mb-2 w-full">{error}</div>

          <div className="flex justify-between">
            <div>
              <BaseButton
                isLoading={isLoading}
                disabled={isLoading}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  close();
                }}
              >
                {cancelText}
              </BaseButton>
            </div>
            <div className="flex gap-2">
              {secondarySubmitText && onSecondarySubmit && (
                <BaseButton
                  color="default"
                  isLoading={isLoading}
                  disabled={isLoading}
                  onClick={(event) => {
                    runSubmit(onSecondarySubmit);

                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  {secondarySubmitText}
                </BaseButton>
              )}
              <BaseButton
                color="primary"
                isLoading={isLoading}
                disabled={isLoading}
                onClick={(event) => {
                  runSubmit(onSubmit);

                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                {submitText}
              </BaseButton>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
